import { Box, Flex, Icon, Skeleton, Text, VStack } from '@chakra-ui/react';
import { FiCheck } from '@react-icons/all-files/fi/FiCheck';
import { useState } from 'react';

import { useOnceWhen } from '../../../../../../../hooks';
import MarketplaceCard from '../../../MarketplaceCard';
import OdometerMileageModal from '../OdometerMileageModal';
import ProgressTrack from '../ProgressTrack';
import {
  ModalTodoDefinition,
  TodoDefinition,
  hasReachedSigning,
  isUploadTodo,
  stageCardCopy,
} from '../utils';
import CarPhotosModal from './CarPhotosModal';
import ConfirmAddressModal from './ConfirmAddressModal';
import PickupAvailabilityModal from './PickupAvailabilityModal';
import TodoCard from './TodoCard';
import { TodoList } from './useTodoList';

type TodoPanelProps = {
  todoList: TodoList;
  dealershipName: string;
};

const TodoPanel = ({ todoList, dealershipName }: TodoPanelProps) => {
  const {
    lead,
    externalId,
    todos,
    pickupTodo,
    remaining,
    total,
    statusReady,
    isFinished,
    isProcessing,
    uploadForTask,
    isOdometerPromptOpen,
    confirmOdometerUpload,
    cancelOdometerUpload,
  } = todoList;
  const [openTask, setOpenTask] = useState<ModalTodoDefinition['key'] | null>(null);

  const openTaskFor = (definition: TodoDefinition): (() => void) | undefined =>
    isUploadTodo(definition) ? undefined : () => setOpenTask(definition.key);
  const closeTask = () => setOpenTask(null);

  useOnceWhen(pickupTodo?.state === 'todo', () => setOpenTask('pickup_availability'));

  const modals = externalId && (
    <>
      <ConfirmAddressModal
        isOpen={openTask === 'confirm_address'}
        onClose={closeTask}
        lead={lead}
        externalId={externalId}
      />
      <CarPhotosModal isOpen={openTask === 'car_photos'} onClose={closeTask} todoList={todoList} />
      <OdometerMileageModal
        isOpen={isOdometerPromptOpen}
        externalId={externalId}
        onConfirm={confirmOdometerUpload}
        onCancel={cancelOdometerUpload}
      />
      <PickupAvailabilityModal
        isOpen={openTask === 'pickup_availability'}
        onClose={closeTask}
        externalId={externalId}
        pickupTimezone={lead.pickupTimezone}
        dealershipName={dealershipName}
      />
    </>
  );

  // Once contracts go out, leftover to-dos are moot; show the stage card instead.
  if (isFinished || hasReachedSigning(lead.status)) {
    const finishedCopy = stageCardCopy(lead, dealershipName);
    return (
      <MarketplaceCard as="div" p={{ base: '20px 18px', md: '24px 26px' }}>
        <VStack spacing={0} textAlign="center" py={{ base: '28px', md: '44px' }}>
          <Flex
            w="60px"
            h="60px"
            borderRadius="full"
            align="center"
            justify="center"
            bg="leaseEndGreen"
            color="white"
          >
            <Icon as={FiCheck} boxSize="30px" />
          </Flex>
          <Text
            fontFamily="heading"
            fontSize="20px"
            fontWeight={700}
            color="leaseEndBlue"
            mt="20px"
          >
            {finishedCopy.headline}
          </Text>
          <Text fontSize="14px" lineHeight="1.6" color="grayMedium" maxW="48ch" mt="8px">
            {finishedCopy.subcopy}
          </Text>
        </VStack>

        {pickupTodo && (
          <TodoCard
            item={pickupTodo}
            isProcessing={false}
            onUpload={() => undefined}
            onOpen={() => setOpenTask('pickup_availability')}
          />
        )}
        {modals}
      </MarketplaceCard>
    );
  }

  const completed = total - remaining;
  const progress = (completed / total) * 100;

  return (
    <MarketplaceCard as="div" p={{ base: '20px 18px', md: '24px 26px' }}>
      <Flex align="center" justify="space-between" gap="12px">
        <Text fontFamily="heading" fontSize="16px" fontWeight={700} color="leaseEndBlue">
          Your to-dos
        </Text>
        {statusReady && (
          <Text fontSize="12px" fontWeight={600} color="grayMedium" flex="none">
            {remaining} to go
          </Text>
        )}
      </Flex>
      <Text fontSize="13px" lineHeight="1.5" color="charcoal" mt="4px">
        Finish these so we can prep your paperwork and schedule pickup.
      </Text>

      {statusReady && completed > 0 && (
        <Box mt="14px">
          <ProgressTrack progress={progress} />
        </Box>
      )}

      <Flex direction="column" gap="9px" mt="16px">
        {todos.map((todo) =>
          statusReady ? (
            <TodoCard
              key={todo.definition.key}
              item={todo}
              isProcessing={isProcessing(todo.definition)}
              onUpload={(file) => uploadForTask(todo.definition, file)}
              onOpen={openTaskFor(todo.definition)}
            />
          ) : (
            <Skeleton key={todo.definition.key} h="64px" borderRadius="12px" />
          ),
        )}
      </Flex>
      {modals}
    </MarketplaceCard>
  );
};

export default TodoPanel;
